import type {
  SessionsResponse,
  SessionDetailResponse,
  SetSessionHiddenResponse,
  CreateJobResponse,
  CancelJobResponse,
  AnswerQuestionResponse,
  Filters,
  UserPreferencesResponse,
  UpdateUserPreferencesInput,
  ModelsResponse,
  SlackChannelsResponse,
  BroadcastResponse,
  UploadResponse,
  TranscriptionResponse,
  CircuitListOwnerScope,
  ManagedOwnerScope,
  UsageStatsResponse,
  CapabilityPerformanceResponse,
  SlowCallsResponse,
  SlowRunsResponse,
  MoriartyEnvironmentsResponse,
  MoriartyEnvironmentResponse,
  MoriartyProntoStatusesResponse,
  MoriartyConfigResponse,
  MoriartyModelAuthSyncResponse,
  MoriartySpawnRequest,
  MoriartySpawnResponse,
  DocsListResponse,
  DocResponse,
  GitHubReviewRunDetailResponse,
  GitHubReviewRunsResponse,
  CodexResponse,
  CodexRuleResponse,
  CliReleaseManifest,
  CircuitDefinition,
  CircuitCronOverlapPolicy,
  CircuitDeleteResponse,
  CircuitDetailResponse,
  CircuitMutationResponse,
  CircuitRunResponse,
  CircuitStatus,
  CircuitTriggerConfig,
  CircuitWebhookCredentialResponse,
  CircuitsResponse,
} from "./types";

const API_BASE = "/v1";

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers || {});
  const isFormData = options?.body instanceof FormData;
  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function fetchCliRelease(): Promise<CliReleaseManifest> {
  return fetchJson<CliReleaseManifest>(`${API_BASE}/cli/releases/latest`);
}

export async function fetchSessions(filters: Filters): Promise<SessionsResponse> {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.source) params.set("source", filters.source);
  if (filters.search) params.set("search", filters.search);
  if (filters.person) params.set("person", filters.person);
  if (filters.mine) params.set("mine", "true");
  if (filters.includeAutomated) params.set("includeAutomated", "true");
  if (filters.limit) params.set("limit", String(filters.limit));

  const url = params.toString()
    ? `${API_BASE}/sessions?${params.toString()}`
    : `${API_BASE}/sessions`;

  return fetchJson<SessionsResponse>(url);
}

export async function fetchSession(threadKey: string): Promise<SessionDetailResponse> {
  const encodedKey = encodeURIComponent(threadKey);
  return fetchJson<SessionDetailResponse>(`${API_BASE}/sessions/${encodedKey}`);
}

export async function setSessionHidden(
  threadKey: string,
  hidden: boolean,
): Promise<SetSessionHiddenResponse> {
  const encodedKey = encodeURIComponent(threadKey);
  return fetchJson<SetSessionHiddenResponse>(`${API_BASE}/sessions/${encodedKey}/hidden`, {
    method: "PATCH",
    body: JSON.stringify({ hidden }),
  });
}

export async function fetchUserPreferences(): Promise<UserPreferencesResponse> {
  return fetchJson<UserPreferencesResponse>(`${API_BASE}/me/preferences`);
}

export async function updateUserPreferences(
  data: UpdateUserPreferencesInput,
): Promise<UserPreferencesResponse> {
  return fetchJson<UserPreferencesResponse>(`${API_BASE}/me/preferences`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function fetchModels(): Promise<ModelsResponse> {
  return fetchJson<ModelsResponse>(`${API_BASE}/models`);
}

export async function fetchUsageStats(days = 14, mine = true): Promise<UsageStatsResponse> {
  const params = new URLSearchParams();
  params.set("days", String(days));
  if (mine) {
    params.set("mine", "true");
  }
  return fetchJson<UsageStatsResponse>(`${API_BASE}/usage/stats?${params.toString()}`);
}

export function fetchCapabilityPerformance(days: number, mine: boolean, profile: string) {
  const query = new URLSearchParams({ days: String(days), mine: String(mine) });
  if (profile) query.set("profile", profile);
  return fetchJson<CapabilityPerformanceResponse>(
    `${API_BASE}/usage/performance/capabilities?${query}`,
  );
}

export function fetchSlowCalls(days: number, mine: boolean, capabilityId: string, profile: string) {
  const query = new URLSearchParams({
    days: String(days),
    mine: String(mine),
    capabilityId,
    profile,
  });
  return fetchJson<SlowCallsResponse>(`${API_BASE}/usage/performance/calls?${query}`);
}

export function fetchSlowRuns(days: number, mine: boolean, kind: string) {
  const query = new URLSearchParams({ days: String(days), mine: String(mine), kind });
  return fetchJson<SlowRunsResponse>(`${API_BASE}/usage/performance/runs?${query}`);
}

export async function createJob(
  message: string,
  threadKey?: string,
  modelId?: string,
  attachmentIds?: string[],
): Promise<CreateJobResponse> {
  return fetchJson<CreateJobResponse>(`${API_BASE}/jobs`, {
    method: "POST",
    body: JSON.stringify({ message, threadKey, modelId, attachmentIds }),
  });
}

export async function uploadAttachments(files: File[]): Promise<UploadResponse> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }

  return fetchJson<UploadResponse>(`${API_BASE}/uploads`, {
    method: "POST",
    body: formData,
  });
}

export async function transcribeAudio(
  audioFile: File,
  signal?: AbortSignal,
): Promise<TranscriptionResponse> {
  const formData = new FormData();
  formData.append("audio", audioFile);

  return fetchJson<TranscriptionResponse>(`${API_BASE}/transcriptions/transcribe`, {
    method: "POST",
    body: formData,
    signal,
  });
}

export async function cleanupTranscription(
  text: string,
  signal?: AbortSignal,
): Promise<TranscriptionResponse> {
  return fetchJson<TranscriptionResponse>(`${API_BASE}/transcriptions/cleanup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
    signal,
  });
}

export async function retryJobDelivery(
  jobId: string,
  confirmedNotDelivered: boolean,
): Promise<{ jobId: string }> {
  return fetchJson(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/retry-delivery`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirmedNotDelivered }),
  });
}

export async function cancelJob(jobId: string): Promise<CancelJobResponse> {
  return fetchJson<CancelJobResponse>(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: "POST",
  });
}

export async function answerQuestion(
  jobId: string,
  answers: string[][],
): Promise<AnswerQuestionResponse> {
  return fetchJson<AnswerQuestionResponse>(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/answer`, {
    method: "POST",
    body: JSON.stringify({ answers }),
  });
}

export async function fetchSlackChannels(): Promise<SlackChannelsResponse> {
  return fetchJson<SlackChannelsResponse>(`${API_BASE}/slack/channels`);
}

export async function broadcastToSlack(
  channel: string,
  message: string,
): Promise<BroadcastResponse> {
  return fetchJson<BroadcastResponse>(`${API_BASE}/slack/broadcast`, {
    method: "POST",
    body: JSON.stringify({ channel, message }),
  });
}

export async function fetchGitHubReviewRuns(): Promise<GitHubReviewRunsResponse> {
  return fetchJson<GitHubReviewRunsResponse>(`${API_BASE}/github-review-runs`);
}

export async function fetchGitHubReviewRun(id: string): Promise<GitHubReviewRunDetailResponse> {
  return fetchJson<GitHubReviewRunDetailResponse>(
    `${API_BASE}/github-review-runs/${encodeURIComponent(id)}`,
  );
}

export async function fetchCodexRule(ruleId: string): Promise<CodexRuleResponse> {
  return fetchJson<CodexRuleResponse>(`${API_BASE}/codex/${encodeURIComponent(ruleId)}`);
}

export async function fetchCodex(): Promise<CodexResponse> {
  return fetchJson<CodexResponse>(`${API_BASE}/codex`);
}

export async function fetchCircuits(
  ownerScope: CircuitListOwnerScope = "personal",
  status?: CircuitStatus,
): Promise<CircuitsResponse> {
  const params = new URLSearchParams({ owner: ownerScope });
  if (status) params.set("status", status);
  return fetchJson<CircuitsResponse>(`${API_BASE}/circuits?${params.toString()}`);
}

export async function fetchCircuit(id: string): Promise<CircuitDetailResponse> {
  return fetchJson<CircuitDetailResponse>(`${API_BASE}/circuits/${encodeURIComponent(id)}`);
}

export async function createCircuit(
  data: {
    name: string;
    cronOverlapPolicy?: CircuitCronOverlapPolicy;
    description?: string;
    definition: CircuitDefinition;
    triggers: CircuitTriggerConfig[];
  },
  ownerScope: ManagedOwnerScope = "personal",
): Promise<CircuitMutationResponse> {
  return fetchJson<CircuitMutationResponse>(`${API_BASE}/circuits?owner=${ownerScope}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateCircuit(
  id: string,
  data: {
    name?: string;
    cronOverlapPolicy?: CircuitCronOverlapPolicy;
    description?: string | null;
    definition?: CircuitDefinition;
    triggers?: CircuitTriggerConfig[];
    status?: CircuitStatus;
  },
): Promise<CircuitMutationResponse> {
  return fetchJson<CircuitMutationResponse>(`${API_BASE}/circuits/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteCircuit(id: string): Promise<CircuitDeleteResponse> {
  return fetchJson<CircuitDeleteResponse>(`${API_BASE}/circuits/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function triggerCircuit(id: string): Promise<CircuitRunResponse> {
  return fetchJson<CircuitRunResponse>(`${API_BASE}/circuits/${encodeURIComponent(id)}/trigger`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function rotateCircuitWebhook(
  id: string,
  webhookId: string,
): Promise<CircuitWebhookCredentialResponse> {
  return fetchJson<CircuitWebhookCredentialResponse>(
    `${API_BASE}/circuits/${encodeURIComponent(id)}/webhooks/${encodeURIComponent(webhookId)}/rotate`,
    { method: "POST" },
  );
}

export async function cancelCircuitRun(id: string, runId: string): Promise<CircuitRunResponse> {
  return fetchJson<CircuitRunResponse>(
    `${API_BASE}/circuits/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}/cancel`,
    { method: "POST" },
  );
}

// Moriarty API functions
export async function fetchMoriartyEnvironments(filters?: {
  status?: string;
  includeTerminated?: boolean;
}): Promise<MoriartyEnvironmentsResponse> {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  if (filters?.includeTerminated) params.set("include_terminated", "true");
  const qs = params.toString();
  return fetchJson<MoriartyEnvironmentsResponse>(
    `${API_BASE}/moriarty/environments${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchMoriartyEnvironment(id: string): Promise<MoriartyEnvironmentResponse> {
  return fetchJson<MoriartyEnvironmentResponse>(
    `${API_BASE}/moriarty/environments/${encodeURIComponent(id)}`,
  );
}

export async function fetchMoriartyProntoStatuses(): Promise<MoriartyProntoStatusesResponse> {
  return fetchJson<MoriartyProntoStatusesResponse>(`${API_BASE}/moriarty/pronto-statuses`);
}

export async function fetchMoriartyConfig(): Promise<MoriartyConfigResponse> {
  return fetchJson<MoriartyConfigResponse>(`${API_BASE}/moriarty/config`);
}

export async function updateMoriartyConfig(
  config: Record<string, string>,
): Promise<MoriartyConfigResponse> {
  return fetchJson<MoriartyConfigResponse>(`${API_BASE}/moriarty/config`, {
    method: "PATCH",
    body: JSON.stringify(config),
  });
}

export async function spawnMoriartyEnvironment(
  data: MoriartySpawnRequest,
): Promise<MoriartySpawnResponse> {
  return fetchJson<MoriartySpawnResponse>(`${API_BASE}/moriarty/spawn`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function destroyMoriartyEnvironment(id: string): Promise<{ ok: boolean }> {
  return fetchJson<{ ok: boolean }>(`${API_BASE}/moriarty/environments/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function sendMoriartyEnvironmentPrompt(
  id: string,
): Promise<MoriartyEnvironmentResponse> {
  return fetchJson<MoriartyEnvironmentResponse>(
    `${API_BASE}/moriarty/environments/${encodeURIComponent(id)}/prompt`,
    {
      method: "POST",
    },
  );
}

export async function wakeMoriartyEnvironment(
  id: string,
): Promise<MoriartyEnvironmentResponse & { reused: boolean }> {
  return fetchJson<MoriartyEnvironmentResponse & { reused: boolean }>(
    `${API_BASE}/moriarty/environments/${encodeURIComponent(id)}/wake`,
    { method: "POST" },
  );
}

export async function sleepMoriartyEnvironment(
  id: string,
): Promise<MoriartyEnvironmentResponse & { reused: boolean }> {
  return fetchJson<MoriartyEnvironmentResponse & { reused: boolean }>(
    `${API_BASE}/moriarty/environments/${encodeURIComponent(id)}/sleep`,
    { method: "POST" },
  );
}

export async function keepMoriartyEnvironmentAwake(
  id: string,
  minutes = 60,
): Promise<MoriartyEnvironmentResponse> {
  return fetchJson<MoriartyEnvironmentResponse>(
    `${API_BASE}/moriarty/environments/${encodeURIComponent(id)}/keep-awake`,
    { method: "POST", body: JSON.stringify({ minutes }) },
  );
}

export async function syncMoriartyModelCredentials(
  id: string,
): Promise<MoriartyModelAuthSyncResponse> {
  return fetchJson<MoriartyModelAuthSyncResponse>(
    `${API_BASE}/moriarty/environments/${encodeURIComponent(id)}/sync-model-credentials`,
    { method: "POST" },
  );
}

// Docs API functions
export async function fetchDocsList(): Promise<DocsListResponse> {
  return fetchJson<DocsListResponse>(`${API_BASE}/docs`);
}

export async function fetchDoc(slug: string): Promise<DocResponse> {
  return fetchJson<DocResponse>(`${API_BASE}/docs/${encodeURIComponent(slug)}`);
}

// Admin API functions

export type AdminSecret = {
  key: string;
  category: string | null;
  description: string | null;
  updatedAt: number;
  updatedBy: string;
};

export type AdminSecretDetail = AdminSecret & {
  value: string;
};

export type AdminUser = {
  id: string;
  name: string;
  email: string;
  role: "admin" | "member";
  image: string | null;
  disabledAt: string | null;
  disabledBy: string | null;
  createdAt: string;
};

export type AdminCapability = {
  id: string;
  description: string;
  access: "read" | "write";
  destructive: boolean;
  enabledByDefault?: boolean;
};

export type AdminCapabilityAccess = {
  capabilities: AdminCapability[];
  overrides: Array<{ userId: string; capabilityId: string; enabled: boolean }>;
};

export type AdminMcpServerStatus = {
  name: string;
  status: "connected" | "disabled" | "failed" | "needs_auth" | "needs_client_registration";
  enabled: boolean;
  available: boolean;
  authState: "ready" | "required" | "client_registration_required" | "not_checked" | "unknown";
  error: string | null;
};

export type AdminMcpStatusResponse = {
  servers: AdminMcpServerStatus[];
  checkedAt: string;
};

export async function fetchAdminSecrets(): Promise<{ secrets: AdminSecret[] }> {
  return fetchJson(`${API_BASE}/admin/secrets`);
}

export async function fetchAdminSecret(key: string): Promise<AdminSecretDetail> {
  return fetchJson(`${API_BASE}/admin/secrets/${encodeURIComponent(key)}`);
}

export async function saveAdminSecret(
  key: string,
  data: { value: string; category?: string; description?: string },
): Promise<{ ok: boolean; key: string }> {
  return fetchJson(`${API_BASE}/admin/secrets/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAdminSecret(key: string): Promise<{ ok: boolean; key: string }> {
  return fetchJson(`${API_BASE}/admin/secrets/${encodeURIComponent(key)}`, {
    method: "DELETE",
  });
}

export async function fetchAdminUsers(): Promise<{ users: AdminUser[] }> {
  return fetchJson(`${API_BASE}/admin/users`);
}

export async function updateAdminUserRole(
  id: string,
  role: "admin" | "member",
): Promise<{ ok: boolean; id: string; role: string }> {
  return fetchJson(`${API_BASE}/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export async function updateAdminUserStatus(
  id: string,
  active: boolean,
): Promise<{
  ok: boolean;
  id: string;
  active: boolean;
  disabledCircuits: number;
}> {
  return fetchJson(`${API_BASE}/admin/users/${id}/status`, {
    method: "PUT",
    body: JSON.stringify({ active }),
  });
}

export async function fetchAdminCapabilities(): Promise<AdminCapabilityAccess> {
  return fetchJson(`${API_BASE}/admin/capabilities`);
}

export async function fetchAdminMcpStatus(): Promise<AdminMcpStatusResponse> {
  return fetchJson(`${API_BASE}/admin/mcp`);
}

export async function updateAdminUserCapability(
  userId: string,
  capabilityId: string,
  enabled: boolean,
): Promise<{ ok: boolean; userId: string; capabilityId: string; enabled: boolean }> {
  return fetchJson(
    `${API_BASE}/admin/users/${userId}/capabilities/${encodeURIComponent(capabilityId)}`,
    {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    },
  );
}

export async function updateAllAdminUserCapabilities(
  userId: string,
  enabled: boolean,
): Promise<{ ok: boolean; userId: string; enabled: boolean; capabilityCount: number }> {
  return fetchJson(`${API_BASE}/admin/users/${userId}/capabilities`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

export async function confirmCliAuth(userCode: string): Promise<{ ok: boolean }> {
  return fetchJson("/api/auth/cli/confirm", {
    method: "POST",
    body: JSON.stringify({ userCode }),
  });
}
